import { useRef, useState } from "react";

export function BookingSummaryCard({ booking: foundBooking }) {
  return (
    <div className="card mb-4" style={{ background: "var(--pine-subtle)", border: "1px solid rgba(42,68,54,.25)" }} data-testid="checkin-booking-summary">
      <div className="eyebrow mb-2" style={{ color: "var(--pine)" }}>Reservation found</div>
      <div className="font-serif text-2xl">Welcome, {foundBooking.first_name}</div>
      <div className="text-sm mt-1">Booking {foundBooking.booking_reference} · {foundBooking.check_in_date} → {foundBooking.check_out_date} · {foundBooking.total_pax} guests</div>
      <div className="text-sm mt-2">
        Booking ₹{Number(foundBooking.booking_amount).toLocaleString("en-IN")} · Received ₹{Number(foundBooking.verified_paid).toLocaleString("en-IN")} ·{" "}
        <b style={{ color: "var(--terracotta)" }}>Balance due ₹{Number(foundBooking.balance_remaining).toLocaleString("en-IN")}</b>
      </div>
    </div>
  );
}

export function LookupStep({ lookupMobile, setLookupMobile, bookingRefInput, setBookingRefInput, showRefInput, lookupErr, busy, onLookup, onManual }) {
  return (
    <div className="space-y-4">
      <div className="eyebrow">Find Your Reservation</div>
      <p className="text-sm" style={{ color: "var(--muted)" }}>Enter the mobile number used when booking — we'll pull up your reservation.</p>
      <input data-testid="checkin-lookup-mobile" placeholder="Mobile number" value={lookupMobile} onChange={(e) => setLookupMobile(e.target.value)} />
      {showRefInput && (
        <input data-testid="checkin-lookup-ref" placeholder="Booking ID (e.g. FBN-2026-00101)" value={bookingRefInput} onChange={(e) => setBookingRefInput(e.target.value)} />
      )}
      {lookupErr && <div className="text-sm" style={{ color: "var(--terracotta)" }} data-testid="checkin-lookup-error">{lookupErr}</div>}
      <button data-testid="checkin-lookup-btn" onClick={onLookup} disabled={busy || lookupMobile.replace(/\D/g, "").length < 10} className="btn-primary w-full">
        {busy ? "Searching…" : "Find My Booking"}
      </button>
      <button data-testid="checkin-manual" onClick={onManual} className="btn-outline w-full">I don't have a booking — check in manually</button>
    </div>
  );
}

export function GuestInfoStep({ f, setField: set, onEmailChange, emailErr }) {
  return (
    <div className="space-y-4">
      <div className="eyebrow">Step 1 · Guest Information</div>
      <input data-testid="checkin-full-name" placeholder="Full name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
      <input data-testid="checkin-mobile" placeholder="Mobile number" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} />
      <div>
        <input data-testid="checkin-email" type="email" placeholder="Email" value={f.email} onChange={onEmailChange} required />
        {emailErr && <div className="text-xs mt-1" style={{ color: "var(--terracotta)" }} data-testid="checkin-email-error">{emailErr}</div>}
      </div>
      <input data-testid="checkin-dob" type="date" placeholder="Date of birth" value={f.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
    </div>
  );
}

export function StayDetailsStep({ f, setField: set }) {
  return (
    <div className="space-y-4">
      <div className="eyebrow">Step 2 · Stay Details</div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs" style={{ color: "var(--muted)" }}>Adults</label><input data-testid="checkin-adults" type="number" min="1" value={f.adults} onChange={(e) => set("adults", parseInt(e.target.value || "1"))} /></div>
        <div><label className="text-xs" style={{ color: "var(--muted)" }}>Children</label><input data-testid="checkin-children" type="number" min="0" value={f.children} onChange={(e) => set("children", parseInt(e.target.value || "0"))} /></div>
        <div><label className="text-xs" style={{ color: "var(--muted)" }}>Check-in</label><input data-testid="checkin-in-date" type="date" value={f.check_in_date} onChange={(e) => set("check_in_date", e.target.value)} /></div>
        <div><label className="text-xs" style={{ color: "var(--muted)" }}>Check-out</label><input data-testid="checkin-out-date" type="date" value={f.check_out_date} onChange={(e) => set("check_out_date", e.target.value)} /></div>
      </div>
      <input data-testid="checkin-travel-from" placeholder="Coming from (city)" value={f.travel_from} onChange={(e) => set("travel_from", e.target.value)} />
      <input data-testid="checkin-travel-to" placeholder="Heading to (city)" value={f.travel_to} onChange={(e) => set("travel_to", e.target.value)} />
    </div>
  );
}

export function PreferencesStep({ f, setField: set }) {
  return (
    <div className="space-y-4">
      <div className="eyebrow">Step 3 · Preferences</div>
      <select data-testid="checkin-food-pref" value={f.food_preferences} onChange={(e) => set("food_preferences", e.target.value)}>
        <option>Vegetarian</option><option>Jain Food</option>
      </select>
      <input data-testid="checkin-allergies" placeholder="Any allergies?" value={f.allergies} onChange={(e) => set("allergies", e.target.value)} />
      <input data-testid="checkin-occasion" placeholder="Special occasion? (birthday, anniversary…)" value={f.special_occasion} onChange={(e) => set("special_occasion", e.target.value)} />
      <textarea data-testid="checkin-requests" rows="3" placeholder="Special requests" value={f.special_requests} onChange={(e) => set("special_requests", e.target.value)} />
    </div>
  );
}

export function IdentityStep({ idCfg, idDocs, requiredCount, busy, onFile, onDelete, err }) {
  const ref = useRef(null);
  const [idType, setIdType] = useState("Aadhaar");
  const [label, setLabel] = useState("");
  const everyAdult = idCfg.id_requirement === "EVERY_ADULT";
  const maxMb = idCfg.max_file_mb || 5;

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > maxMb * 1024 * 1024) return onFile(null, idType, label, `File too large (max ${maxMb} MB)`);
    onFile(f, idType, label, null);
  };

  return (
    <div className="space-y-4">
      <div className="eyebrow">Step 4 · Identity Verification</div>
      <p className="text-sm" style={{ color: "var(--muted)" }}>
        Please upload a valid government-issued photo ID.{everyAdult ? " An ID is required for every adult guest." : " Required for the primary guest (booking holder)."}
      </p>
      {everyAdult && (
        <input data-testid="checkin-id-label" placeholder="Guest name on this ID" value={label} onChange={(e) => setLabel(e.target.value)} />
      )}
      <select data-testid="checkin-id-type" value={idType} onChange={(e) => setIdType(e.target.value)}>
        {(idCfg.id_types || []).map((t) => <option key={t}>{t}</option>)}
      </select>
      <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={pick} data-testid="checkin-id-file" />
      <button data-testid="checkin-id-upload-btn" onClick={() => ref.current?.click()} disabled={busy} className="btn-outline w-full">
        {busy ? "Uploading…" : "Upload ID Document (JPG, PNG or PDF)"}
      </button>
      {err && <div className="text-sm" style={{ color: "var(--terracotta)" }} data-testid="checkin-id-error">{err}</div>}
      <div data-testid="checkin-id-list" className="space-y-2">
        {idDocs.map((doc) => (
          <div key={doc.id} className="flex items-center justify-between text-sm p-2 rounded" style={{ background: "var(--surface)" }} data-testid={`checkin-id-doc-${doc.id}`}>
            <span>✓ {doc.id_type}{doc.guest_label ? ` · ${doc.guest_label}` : ""}</span>
            <button data-testid={`checkin-id-remove-${doc.id}`} onClick={() => onDelete(doc.id)} className="text-xs" style={{ color: "var(--terracotta)" }}>Remove</button>
          </div>
        ))}
      </div>
      <div className="text-xs" style={{ color: idDocs.length >= requiredCount ? "var(--pine)" : "var(--terracotta)" }} data-testid="checkin-id-progress">
        {idDocs.length} of {requiredCount} required ID document(s) uploaded
      </div>
    </div>
  );
}

export function ConsentStep({ f, setField: set, err }) {
  return (
    <div className="space-y-4">
      <div className="eyebrow">Step 5 · Consent</div>
      <label className="flex items-start gap-3 cursor-pointer"><input data-testid="checkin-privacy" type="checkbox" style={{ width: "auto" }} checked={f.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} /><span className="text-sm">I accept the villa's privacy policy and house rules.</span></label>
      <label className="flex items-start gap-3 cursor-pointer"><input data-testid="checkin-marketing" type="checkbox" style={{ width: "auto" }} checked={f.marketing_consent} onChange={(e) => set("marketing_consent", e.target.checked)} /><span className="text-sm">Send me occasional offers and travel inspiration.</span></label>
      {err && <div className="text-sm" style={{ color: "var(--terracotta)" }}>{err}</div>}
    </div>
  );
}
